import { YeshumanMarketingChrome } from '@/components/landing/yeshuman/yeshuman-marketing-chrome'
import {
  yeshumanAnn,
  yeshumanAnnDim,
  yeshumanDisplayHeading,
  yeshumanEditorial,
  yeshumanEditorialSm,
  yeshumanEditorialTerm,
  yeshumanMeasure,
} from '@/components/landing/yeshuman/yeshuman-typography'
import {
  yeshumanBand,
  yeshumanBandAlt,
  yeshumanContentColumn,
} from '@/components/landing/yeshuman/yeshuman-layout'
import { Button } from '@/components/ui/button'
import { LandingCtaLink } from '@/landing/landing-cta-link'
import { cn } from '@/lib/utils'

const sectionPad = 'py-[clamp(96px,9vw,132px)]'

const ENTRIES = [
  {
    term: 'Harness',
    anyone:
      'A harness is one LangGraph runtime we shipped. Tool calling, memory, the agent loop. Not the product. Not the page. The stack has several.',
    deeper:
      'Use the word for that runtime. Do not use it as the name of Yes Human. It is not another word for agent, and it is not the UI chrome.',
  },
  {
    term: 'Workbench',
    anyone:
      'Our name for the place: several harnesses, plus tools for people and agents that are not themselves a harness. A rubric builder. A prompt builder. A listening-scribe tool.',
    deeper:
      'Yes Human is an AI-native web application with those harnesses inside. Workbench is our coinage for that place, not a category the industry already agrees on.',
  },
  {
    term: 'Connectors',
    anyone: 'How it talks to the systems you already run. You do not have to leave them.',
    deeper:
      'Usually MCP. Also APIs, client adapters, and CLIs on sandboxes. MCP is an implementation detail that belongs here, not above the fold on the home page.',
  },
  {
    term: 'Surface',
    anyone: 'UIs, voice, phone, iOS. Where people actually use it.',
    deeper:
      'An app they sign into, a phone line, a voice session, a panel in software they already have open, or connections other software calls.',
  },
  {
    term: 'Memory',
    anyone:
      'In the main user-facing harness, memory is agent memory about a user. A memory layer. What people tell it stays, so later work can use it.',
    deeper:
      'Onboarding, preferences, and feedback can be collected and queried. Extrapolations have not been made. That is not a further knowledge graph of assertions that can lose.',
  },
  {
    term: 'Evaluation pipeline',
    anyone:
      'Scores come from rubric and methodology layers, not raw model analysis. That product is the judgment. The process itself can be evaluated.',
    deeper:
      'A written rubric is the standard for a hire, an intake, an inspection. Judgment is not a virtue layer sitting on top of the model.',
  },
  {
    term: 'Prototype vs production',
    anyone:
      'Prototyping is exceedingly easy. A prototype has to earn its place: get users. Production is the harder set of decisions after that.',
    deeper:
      'DevOps, infrastructure, scale, privacy, listening, feedback, usability. We do not sneer at prototypes. They are useful. They are not the same job as production.',
  },
  {
    term: 'Software factory',
    anyone:
      'The name for a wider production benefit: use and feedback can go back into how the product is built.',
    deeper:
      'The shipped slice is agent-assisted automations that collect information and feedback and ingest it into backlogs. Not the product name. Not the hero on the home page.',
  },
]

export function YeshumanConceptsWordsPage() {
  return (
    <YeshumanMarketingChrome stickyHeaderContentInset>
      <main id="main" tabIndex={-1} className="outline-none">
        <section
          aria-labelledby="yeshuman-concepts-heading"
          className={cn(sectionPad, yeshumanBand)}
        >
          <div className={yeshumanContentColumn}>
            <div className={cn('mb-7', yeshumanAnn, yeshumanAnnDim)}>Concepts</div>
            <h1 id="yeshuman-concepts-heading" className={cn(yeshumanDisplayHeading, 'max-w-[16em]')}>
              What these words mean.
            </h1>
            <p className={cn(yeshumanEditorial, yeshumanMeasure, 'mt-7')}>
              Same claims as the home page, with a bit more room. Yes Human is an AI-native web
              application with agent harnesses inside. Django, LangGraph, and UI.
            </p>
          </div>
        </section>

        <section className={cn(sectionPad, yeshumanBandAlt)} aria-label="Definitions">
          <div className={yeshumanContentColumn}>
            <dl className="flex flex-col gap-14">
              {ENTRIES.map((entry) => (
                <div key={entry.term}>
                  <dt className={cn('mb-3', yeshumanEditorialTerm)}>{entry.term}</dt>
                  <dd>
                    <p className={cn(yeshumanEditorialSm, yeshumanMeasure)}>{entry.anyone}</p>
                    <p className={cn(yeshumanEditorialSm, yeshumanMeasure, 'mt-3 text-[var(--marketing-muted)]')}>
                      {entry.deeper}
                    </p>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section
          aria-labelledby="yeshuman-concepts-drawing-heading"
          className={cn(sectionPad, yeshumanBand)}
        >
          <div className={yeshumanContentColumn}>
            <h2 id="yeshuman-concepts-drawing-heading" className={cn(yeshumanDisplayHeading, 'max-w-[16em]')}>
              Topology
            </h2>
            <p className={cn(yeshumanEditorial, yeshumanMeasure, 'mt-7')}>
              The system diagram lives on its own page. You can pan and zoom. Some labels on the
              board are further work, not what is shipped.
            </p>
            <div className="mt-10">
              <Button asChild variant="marketingLink" size="marketingLink" pressFeedback>
                <LandingCtaLink to="/concepts/topology">Open Topology</LandingCtaLink>
              </Button>
            </div>
            <p className={cn('mt-16', yeshumanEditorialSm, yeshumanAnnDim)}>
              <LandingCtaLink to="/" className={yeshumanAnn}>
                Back to Yes Human
              </LandingCtaLink>
            </p>
          </div>
        </section>
      </main>
    </YeshumanMarketingChrome>
  )
}
